import { createContext, useContext, useMemo, useState } from 'react'

import { FormInstance } from 'antd'

import { Section, TemplateData } from '@/modules/templates/types/blueprint'

import { Resource } from 'types/api.graphql'

export type RenderOptions = {
  blockGroupBordered?: boolean
  blockGroupPadding?: string
  blockGroupBackground?: string
  renderingInPdf?: boolean
  readOnlyDateInputs?: boolean
  isPreview?: boolean
  showActions?: boolean
  showDefects?: boolean
  showNotes?: boolean
  showAttachments?: boolean
  pdfAppendix?: boolean
  sectionRenderer?: React.FC<{
    section: Section
    index: number
    children?: React.ReactNode
  }>
  // page breaks
  beginSectionOnNewPage?: boolean
  preventPageBreakInsideBlockGroup?: boolean
  preventPageBreakInsideBlock?: boolean
}

export type ConfigOptions = {
  isPublic?: boolean
  contextualDate?: string
}

export type TemplateRendererMode = 'capture' | 'display'

type Value = {
  mode: TemplateRendererMode
  isCapture: boolean
  isTypeTemplate?: boolean
  templateResponseId: string | null
  templateResponseData: TemplateData
  originalTemplateData?: TemplateData
  form: FormInstance
  renderOptions: RenderOptions
  config: ConfigOptions
  extraInfo: Record<string, unknown>
  builderId: string
  projectId: string
  resource: Resource
  setTemplateData: (data: TemplateData) => void
}

type Props = {
  mode: TemplateRendererMode
  form: FormInstance
  isCapture: boolean
  isTypeTemplate?: boolean
  templateResponseId?: string | null
  templateResponseData: TemplateData
  originalTemplateData?: TemplateData
  renderOptions?: RenderOptions
  config?: ConfigOptions
  extraInfo?: Record<string, unknown>
  builderId: string
  projectId: string
  resource: Resource
  children?: React.ReactNode
}

const getRenderOptions = (options: RenderOptions = {}): RenderOptions => {
  return {
    blockGroupBordered: options.blockGroupBordered ?? true,
    blockGroupPadding: options.blockGroupPadding ?? '0.75rem 1rem',
    blockGroupBackground: options.blockGroupBackground ?? '#ffffff',
    renderingInPdf: options.renderingInPdf ?? false,
    readOnlyDateInputs: options.readOnlyDateInputs ?? false,
    showActions: options.showActions ?? true,
    showDefects: options.showDefects ?? true,
    showNotes: options.showNotes ?? true,
    showAttachments: options.showAttachments ?? true,
    pdfAppendix: options.pdfAppendix ?? false,
    sectionRenderer: options.sectionRenderer ?? undefined,
    beginSectionOnNewPage: options.beginSectionOnNewPage ?? true,
    preventPageBreakInsideBlockGroup:
      options.preventPageBreakInsideBlockGroup ?? false,
    preventPageBreakInsideBlock: options.preventPageBreakInsideBlock ?? true,
    isPreview: options.isPreview ?? false,
  }
}

const RendererContext = createContext<Value>(null)

export const TemplateRendererProvider: React.FC<Props> = ({
  mode,
  form,
  isCapture,
  isTypeTemplate,
  templateResponseId,
  templateResponseData,
  originalTemplateData,
  renderOptions,
  config,
  extraInfo,
  builderId,
  projectId,
  resource,
  children,
}) => {
  const [templateData, setTemplateData] = useState(templateResponseData)

  const value: Value = useMemo(() => {
    return {
      mode,
      form,
      isCapture,
      isTypeTemplate,
      templateResponseId,
      templateResponseData: templateData,
      originalTemplateData,
      renderOptions: getRenderOptions(renderOptions),
      extraInfo: extraInfo || {},
      config: config || {},
      builderId,
      projectId,
      resource,
      setTemplateData,
    }
  }, [
    mode,
    form,
    isCapture,
    isTypeTemplate,
    templateResponseId,
    templateData,
    originalTemplateData,
    renderOptions,
    extraInfo,
    config,
    builderId,
    projectId,
    resource,
  ])

  return (
    <RendererContext.Provider value={value}>
      {children}
    </RendererContext.Provider>
  )
}

export const useTemplateRenderer = () => useContext(RendererContext)
